import { useLocalParticipant, LiveKitRoom, useTracks, VideoTrack, useRoomContext, RoomAudioRenderer } from '@livekit/components-react';
import { Track, VideoPresets, RoomEvent, ConnectionState } from 'livekit-client';
import { useEffect } from 'react';
import '@livekit/components-styles';
import SelfView from './SelfView';
import ParticipantView from './ParticipantView';
import ScreenShareView from './ScreenShareView';
import WhiteboardPanel from './WhiteboardPanel';

const ROOM_OPTIONS = {
  adaptiveStream: false,
  dynacast: false,
  // Use native <audio> elements for playback instead of WebAudio mixing.
  // Prevents "AudioContext encountered an error from the audio device" from
  // taking down the room when the output device is briefly unavailable.
  webAudioMix: false,
  videoCaptureDefaults: {
    resolution: VideoPresets.h720.resolution,
  },
  publishDefaults: {
    videoEncoding: VideoPresets.h720.encoding,
    simulcast: true,
    dtx: true,
  },
};

// ---------------------------------------------------------------------------
// LiveKitVideoArea
//
// Props:
//   token, serverUrl      — LiveKit connection credentials
//   onConnected           — called when room connects → set phase=live
//   onDisconnect          — called on unexpected disconnect
//   onLeave               — called when user clicks leave/end
//   isHost                — controls End vs Leave label
//   showWhiteboard        — whiteboard panel open
//   onWhiteboardToggle    — toggle whiteboard open/closed
//   onWhiteboardClose     — close whiteboard (used by the panel X button)
//   activePanel           — 'modules' | 'resources' | 'schedule' | null
//   onPanelToggle         — (panel) => void
//   showWellnessMenu      — wellness popover open
//   onWellnessMenuToggle  — () => void
//   onPatternPulse        — () => void
//   onBreathingBreak      — () => void
// ---------------------------------------------------------------------------
export default function LiveKitVideoArea({
  token,
  serverUrl,
  onConnected,
  onDisconnect,
  onLeave,
  isHost,
  isGuest = false,
  showWhiteboard,
  onWhiteboardToggle,
  onWhiteboardClose,
  activePanel,
  onPanelToggle,
  showWellnessMenu,
  onWellnessMenuToggle,
  onPatternPulse,
  onBreathingBreak,
  onPublishReady,
  onDataReceived,
  isRecording,
  recordingLoading,
  onRecordingToggle,
}) {
  return (
    <LiveKitRoom
      token={token}
      serverUrl={serverUrl}
      options={ROOM_OPTIONS}
      onConnected={onConnected}
      onDisconnected={onDisconnect}
      className="lk-room-container"
    >
      <VideoLayout
        onLeave={onLeave}
        isHost={isHost}
        isGuest={isGuest}
        showWhiteboard={showWhiteboard}
        onWhiteboardToggle={onWhiteboardToggle}
        onWhiteboardClose={onWhiteboardClose}
        activePanel={activePanel}
        onPanelToggle={onPanelToggle}
        showWellnessMenu={showWellnessMenu}
        onWellnessMenuToggle={onWellnessMenuToggle}
        onPatternPulse={onPatternPulse}
        onBreathingBreak={onBreathingBreak}
        onPublishReady={onPublishReady}
        onDataReceived={onDataReceived}
        isRecording={isRecording}
        recordingLoading={recordingLoading}
        onRecordingToggle={onRecordingToggle}
      />
    </LiveKitRoom>
  );
}

// VideoLayout renders inside the Room context so all LiveKit hooks work here.
function VideoLayout({
  onLeave,
  isHost,
  isGuest = false,
  showWhiteboard,
  onWhiteboardToggle,
  activePanel,
  onPanelToggle,
  showWellnessMenu,
  onWellnessMenuToggle,
  onPatternPulse,
  onBreathingBreak,
  onPublishReady,
  onDataReceived,
  isRecording,
  recordingLoading,
  onRecordingToggle,
}) {
  const room = useRoomContext();

  // ── Enable mic + camera after room connects — isolated from connection ─────
  // Do NOT pass audio/video to <LiveKitRoom>: if device capture is attempted
  // during the connection handshake and fails, LiveKit tears down the whole
  // room. By enabling tracks here, a mic/camera failure is catchable and the
  // session stays alive regardless.
  useEffect(() => {
    if (!room) return;

    const enableTracks = async () => {
      const tryEnable = async (fn, label) => {
        try {
          await fn();
        } catch (err) {
          if (err?.name === 'InvalidAccessError') return;
          console.warn(`[LiveKit] ${label} auto-enable failed:`, err?.message);
        }
      };
      await tryEnable(() => room.localParticipant.setMicrophoneEnabled(true), 'mic');
      await tryEnable(() => room.localParticipant.setCameraEnabled(true), 'camera');
    };

    if (room.state === ConnectionState.Connected) {
      enableTracks();
    } else {
      room.once(RoomEvent.Connected, enableTracks);
      return () => room.off(RoomEvent.Connected, enableTracks);
    }
  }, [room]);

  // ── Data channel: expose publish, subscribe to incoming messages ──────────
  useEffect(() => {
    if (!room) return;

    const encode = (payload) => new TextEncoder().encode(JSON.stringify(payload));
    const decode = (bytes) => JSON.parse(new TextDecoder().decode(bytes));

    const publishData = (payload) => {
      try {
        room.localParticipant.publishData(encode(payload), { reliable: true });
      } catch (e) {
        console.warn('[LiveKit] publishData failed:', e.message);
      }
    };

    onPublishReady?.(publishData);

    const handleData = (payload, participant) => {
      try {
        onDataReceived?.(decode(payload), participant);
      } catch { /* malformed message — ignore */ }
    };

    room.on(RoomEvent.DataReceived, handleData);
    return () => room.off(RoomEvent.DataReceived, handleData);
  }, [room, onPublishReady, onDataReceived]);

  const screenShareTracks = useTracks(
    [{ source: Track.Source.ScreenShare, withPlaceholder: false }],
  );
  const isSharing = screenShareTracks.length > 0;

  // Camera tracks for the whiteboard floating strip (all participants incl. local)
  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: false },
  );

  const {
    localParticipant,
    isCameraEnabled,
    isMicrophoneEnabled,
    isScreenShareEnabled,
  } = useLocalParticipant();

  return (
    <div className={`lk-video-layout ${isSharing && !showWhiteboard ? 'lk-video-layout--sharing' : ''}`}>
      <RoomAudioRenderer />
      <div className="lk-main-area">
        {showWhiteboard ? (
          <>
            {/* Whiteboard fills the hero area */}
            <WhiteboardPanel />

            {/* Floating participant strip — top-right corner */}
            <div className="lk-wb-float-strip">
              {cameraTracks.map((trackRef) => {
                const { participant, publication } = trackRef;
                const hasVideo = publication?.track && !publication.isMuted;
                const isLocal = participant.isLocal;
                const name = isLocal ? 'You' : (participant.name || participant.identity);
                const initials = name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
                return (
                  <div key={participant.identity} className="lk-wb-thumb">
                    {hasVideo ? (
                      <VideoTrack
                        trackRef={trackRef}
                        className={`lk-wb-thumb-video${isLocal ? ' lk-wb-thumb-video--mirror' : ''}`}
                      />
                    ) : (
                      <div className="lk-avatar-placeholder">
                        <span className="lk-avatar-initials">{initials}</span>
                      </div>
                    )}
                    <p className="lk-wb-thumb-name">{name}</p>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <ScreenShareView />
            <div className={`lk-participants-wrapper ${isSharing ? 'lk-participants-wrapper--secondary' : ''}`}>
              <ParticipantView />
            </div>
            <SelfView />
          </>
        )}
      </div>

      {/* ── Unified bottom bar ──────────────────────────────────────────── */}
      <div className="lk-unified-bar">

        {/* Wellness popover — anchored above the center section */}
        {showWellnessMenu && (
          <div className="lk-wellness-menu">
            <button
              type="button"
              className="lk-wellness-menu-item"
              onClick={() => { onPatternPulse(); onWellnessMenuToggle(); }}
            >
              <span aria-hidden="true">🧠</span>
              <span>Pattern Pulse</span>
              <span className="lk-wellness-menu-sub">Boost working memory</span>
            </button>
            <button
              type="button"
              className="lk-wellness-menu-item"
              onClick={() => { onBreathingBreak(); onWellnessMenuToggle(); }}
            >
              <span aria-hidden="true">🌿</span>
              <span>Wellness Break</span>
              <span className="lk-wellness-menu-sub">Guided breathing</span>
            </button>
          </div>
        )}

        {/* Left — whiteboard + record + wellness */}
        <div className="lk-bar-left">
          {isHost && (
            <ToolBtn
              label="Whiteboard"
              active={showWhiteboard}
              onClick={() => {
                if (showWellnessMenu) onWellnessMenuToggle();
                onWhiteboardToggle();
              }}
            >
              <svg className="lk-tool-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <rect x="2" y="3" width="16" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
                <path d="M5 13l2.5-4 2 2.5 2-3.5 2 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </ToolBtn>
          )}
          {isHost && (
            <ToolBtn
              label={isRecording ? 'Stop Rec' : 'Record'}
              active={isRecording}
              disabled={recordingLoading}
              onClick={onRecordingToggle}
              recording={isRecording}
            >
              <RecordIcon active={isRecording} />
            </ToolBtn>
          )}
          <ToolBtn
            label="Wellness"
            active={showWellnessMenu}
            onClick={() => { if (activePanel) onPanelToggle(activePanel); onWellnessMenuToggle(); }}
          >
            <svg className="lk-tool-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M10 16.5S2.5 12 2.5 6.5a4 4 0 017.5-2 4 4 0 017.5 2c0 5.5-7.5 10-7.5 10z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
            </svg>
          </ToolBtn>
        </div>

        {/* Center — meeting controls */}
        <div className="lk-bar-meeting">
          <button
            type="button"
            className={`lk-control-btn ${isMicrophoneEnabled ? 'lk-control-btn--active' : 'lk-control-btn--muted'}`}
            onClick={() => {
              localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled).catch((err) => {
                if (err?.name === 'InvalidAccessError') return;
                console.warn('[LiveKit] mic toggle failed:', err?.message);
              });
            }}
            title={isMicrophoneEnabled ? 'Mute microphone' : 'Unmute microphone'}
          >
            <MicIcon muted={!isMicrophoneEnabled} />
            <span className="lk-control-label">{isMicrophoneEnabled ? 'Mute' : 'Unmute'}</span>
          </button>

          <button
            type="button"
            className={`lk-control-btn ${isCameraEnabled ? 'lk-control-btn--active' : 'lk-control-btn--muted'}`}
            onClick={() => {
              localParticipant.setCameraEnabled(!isCameraEnabled).catch((err) => {
                if (err?.name === 'InvalidAccessError') return;
                console.warn('[LiveKit] camera toggle failed:', err?.message);
              });
            }}
            title={isCameraEnabled ? 'Turn off camera' : 'Turn on camera'}
          >
            <CameraIcon off={!isCameraEnabled} />
            <span className="lk-control-label">{isCameraEnabled ? 'Camera' : 'No cam'}</span>
          </button>

          <button
            type="button"
            className={`lk-control-btn ${isScreenShareEnabled ? 'lk-control-btn--active' : ''}`}
            onClick={() => {
              localParticipant.setScreenShareEnabled(!isScreenShareEnabled).catch((err) => {
                // User cancelled the picker or denied permission — not an error worth surfacing
                if (err?.name === 'NotAllowedError' || /permission denied/i.test(err?.message)) return;
                console.warn('[LiveKit] screen share failed:', err?.message);
              });
            }}
            title={isScreenShareEnabled ? 'Stop sharing' : 'Share screen'}
          >
            <ScreenShareIcon active={isScreenShareEnabled} />
            <span className="lk-control-label">{isScreenShareEnabled ? 'Stop share' : 'Share'}</span>
          </button>

        </div>

        {/* Right — modules, resources, schedule, end */}
        <div className="lk-bar-right">
          {!isGuest && (
            <ToolBtn
              label="Modules"
              active={activePanel === 'modules'}
              onClick={() => window.open('/learn', '_blank')}
            >
              <svg className="lk-tool-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M4 4h7a2 2 0 012 2v9a2 2 0 01-2 2H4V4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
                <path d="M13 4h1a2 2 0 012 2v9a2 2 0 01-2 2h-1" stroke="currentColor" strokeWidth="1.4" />
                <path d="M7 8h4M7 11h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
            </ToolBtn>
          )}

          <ToolBtn
            label="Resources"
            active={activePanel === 'resources'}
            onClick={() => { if (showWellnessMenu) onWellnessMenuToggle(); onPanelToggle('resources'); }}
          >
            <svg className="lk-tool-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M8 13H5a4 4 0 010-8h3M12 7h3a4 4 0 010 8h-3M7 10h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </ToolBtn>

          <ToolBtn
            label="Schedule"
            active={activePanel === 'schedule'}
            onClick={() => { if (showWellnessMenu) onWellnessMenuToggle(); onPanelToggle('schedule'); }}
          >
            <svg className="lk-tool-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <rect x="3" y="4" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.4" />
              <path d="M3 8h14M7 2v4M13 2v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              <rect x="6" y="11" width="3" height="3" rx="0.5" fill="currentColor" />
            </svg>
          </ToolBtn>

          <button
            type="button"
            className="lk-control-btn lk-control-btn--leave"
            onClick={onLeave}
            title={isHost ? 'End session for everyone' : 'Leave session'}
          >
            <LeaveIcon />
            <span className="lk-control-label">{isHost ? 'End' : 'Leave'}</span>
          </button>
        </div>
      </div>

    </div>
  );
}

// ── Reusable tool button ────────────────────────────────────────────────────
function ToolBtn({ children, label, active, disabled, onClick, recording }) {
  let className = 'lk-tool-btn';
  if (recording) className += ' lk-tool-btn--recording';
  else if (active) className += ' lk-tool-btn--active';
  return (
    <button
      type="button"
      className={className}
      disabled={disabled}
      onClick={onClick}
      title={label}
    >
      {children}
      <span className="lk-tool-btn-label">{label}</span>
    </button>
  );
}

// ── SVG icons ───────────────────────────────────────────────────────────────

function RecordIcon({ active }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="lk-icon" aria-hidden="true">
      <circle cx="12" cy="12" r="7" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="3.5" fill={active ? '#fff' : 'currentColor'} />
    </svg>
  );
}

function MicIcon({ muted }) {
  return muted ? (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lk-icon" aria-hidden="true">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lk-icon" aria-hidden="true">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function CameraIcon({ off }) {
  return off ? (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lk-icon" aria-hidden="true">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3m3-3h6l2 3h3a2 2 0 0 1 2 2v9.34" />
      <circle cx="12" cy="13" r="3" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lk-icon" aria-hidden="true">
      <path d="M23 7l-7 5 7 5V7z" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  );
}

function ScreenShareIcon({ active }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lk-icon" aria-hidden="true">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" fill={active ? 'currentColor' : 'none'} stroke="currentColor" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function LeaveIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lk-icon" aria-hidden="true">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
